<script setup lang="ts">
import { computed } from 'vue'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import LoginCard from '@/components/ui/LoginCard.vue'
import ProductChoiceCard from '@/components/ui/ProductChoiceCard.vue'
import { productHref, HOME_PRODUCTS, type ProductKey } from '@/router/products'
import { DEFAULT_LOCALE, type Locale } from '@/i18n'

const { t } = useI18n()
const route = useRoute()
const currentLocale = computed<Locale>(() => route.meta.locale ?? DEFAULT_LOCALE)

/** Pictos par produit (noms de fichiers sans extension dans src/assets/icons/).
 *  L'ordre et les liens des produits sont partagés via HOME_PRODUCTS. */
const productIcons: Record<ProductKey, readonly string[]> = {
  healthExpenses:  ['Picto_depense_sante_orange', 'Picto_dent_orange', 'Picto_lunettes_orange'],
  housing:         ['Picto_smerra-logement-etudiant_orange'],
  civil:           ['Picto_assurance_RC_orange'],
  abroad:          ['Picto_smerra_voyage_orange'],
  arrivingFrance:  ['valises'],
  healthTopUp:     ['Picto_engagements-etudiants_orange'],
  regionalAura:    ['Picto_depense_sante_orange'],
  solidariteExam:  ['Picto_engagements-etudiants_orange'],
}

const products = HOME_PRODUCTS
</script>

<template>
  <section class="choice-panel section">
    <div class="container">
      <div class="choice-panel__grid">
        <aside class="choice-panel__col-login">
          <LoginCard />
        </aside>

        <div class="choice-panel__col-products">
          <header class="choice-panel__head">
            <span class="eyebrow eyebrow--display text-stroke">
              {{ t('subscription.products.eyebrow') }}
            </span>
            <h2>{{ t('subscription.products.title') }}</h2>
          </header>

          <div class="choice-panel__cards">
            <ProductChoiceCard
              v-for="p in products"
              :key="p.key"
              :icons="productIcons[p.key]"
              :label="t(`subscription.products.items.${p.key}`)"
              :href="p.externalHref ?? productHref(p.key, currentLocale)"
              :external="Boolean(p.externalHref)"
            />
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<style scoped>
.choice-panel {
  position: relative;
  background: transparent;
}

.choice-panel__grid {
  display: grid;
  gap: var(--space-8);
  grid-template-columns: 1fr;
}
@media (min-width: 56rem) {
  .choice-panel__grid {
    grid-template-columns: 17rem minmax(0, 1fr);
    gap: var(--space-8);
    align-items: start;
  }
}

.choice-panel__col-login,
.choice-panel__col-products {
  display: flex;
  flex-direction: column;
  min-width: 0;
}
@media (min-width: 56rem) {
  .choice-panel__col-login {
    position: sticky;
    top: calc(var(--topbar-h) + 4.5rem + var(--space-4));
  }
}

.choice-panel__head {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  margin-bottom: var(--space-6);
}
.choice-panel__head h2 {
  font-size: var(--text-3xl);
}

.choice-panel__cards {
  display: grid;
  gap: 0.875rem;
  grid-template-columns: 1fr;
}
@media (min-width: 30rem) {
  .choice-panel__cards { grid-template-columns: repeat(2, 1fr); }
}
@media (min-width: 80rem) {
  .choice-panel__cards { grid-template-columns: repeat(3, 1fr); }
}
</style>
